import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, ChevronLeft, ChevronRight, Video, MapPin, User as UserIcon, Clock, ExternalLink, Users } from "lucide-react";

const VIEWS = [
  { key: "month", label: "Month" },
  { key: "week", label: "Week" },
  { key: "day", label: "Day" },
  { key: "list", label: "List" },
];

// Consistent per-owner colour based on id
const PALETTE = [
  { bg: "rgba(0,122,255,0.18)", fg: "#4DA6FF", bar: "#4DA6FF" },
  { bg: "rgba(37,211,102,0.18)", fg: "#25D366", bar: "#25D366" },
  { bg: "rgba(251,191,36,0.18)", fg: "#FBBF24", bar: "#FBBF24" },
  { bg: "rgba(236,72,153,0.18)", fg: "#F472B6", bar: "#F472B6" },
  { bg: "rgba(168,85,247,0.18)", fg: "#A78BFA", bar: "#A78BFA" },
  { bg: "rgba(56,189,248,0.18)", fg: "#38BDF8", bar: "#38BDF8" },
];
function colorFor(id) {
  if (!id) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d) { const x = startOfDay(d); const day = (x.getDay() + 6) % 7; return addDays(x, -day); } // Monday
function startOfMonth(d) { const x = startOfDay(d); x.setDate(1); return x; }
function endOfMonth(d) { const x = startOfMonth(d); x.setMonth(x.getMonth() + 1); return addDays(x, -1); }
function sameDay(a, b) { return startOfDay(a).getTime() === startOfDay(b).getTime(); }
function fmtTime(d) { return new Date(d).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
function fmtHeader(d) { return new Date(d).toLocaleDateString(undefined, { month: "long", year: "numeric" }); }

export default function Calendar() {
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(startOfDay(new Date()));
  const [ownerFilter, setOwnerFilter] = useState("all");

  const { data: salespeople = [] } = useQuery({
    queryKey: ["calendar-salespeople"],
    queryFn: () => api.get("/calendar/salespeople").then((r) => r.data),
  });

  // Compute range for API based on view
  const { rangeStart, rangeEnd } = useMemo(() => {
    if (view === "month") {
      const s = addDays(startOfWeek(startOfMonth(anchor)), 0);
      const e = addDays(s, 42);
      return { rangeStart: s, rangeEnd: e };
    }
    if (view === "week") {
      const s = startOfWeek(anchor);
      return { rangeStart: s, rangeEnd: addDays(s, 7) };
    }
    if (view === "day") {
      return { rangeStart: startOfDay(anchor), rangeEnd: addDays(startOfDay(anchor), 1) };
    }
    // list: show 60 days from anchor
    return { rangeStart: startOfDay(anchor), rangeEnd: addDays(startOfDay(anchor), 60) };
  }, [view, anchor]);

  const { data: meetings = [], isLoading } = useQuery({
    queryKey: ["calendar-meetings", rangeStart.toISOString(), rangeEnd.toISOString(), ownerFilter],
    queryFn: () => api.get("/calendar/meetings", {
      params: {
        date_from: rangeStart.toISOString(),
        date_to: rangeEnd.toISOString(),
        user_id: ownerFilter === "all" ? undefined : ownerFilter,
      },
    }).then((r) => r.data),
  });

  const nav = (dir) => {
    if (view === "month") { const x = new Date(anchor); x.setMonth(x.getMonth() + dir); setAnchor(startOfDay(x)); }
    else if (view === "week") setAnchor(addDays(anchor, 7 * dir));
    else if (view === "day") setAnchor(addDays(anchor, 1 * dir));
    else setAnchor(addDays(anchor, 7 * dir));
  };
  const today = () => setAnchor(startOfDay(new Date()));

  const upcoming = useMemo(() => meetings.filter((m) => new Date(m.start_at) >= startOfDay(new Date())).slice(0, 6), [meetings]);

  const label = useMemo(() => {
    if (view === "month") return fmtHeader(anchor);
    if (view === "week") {
      const s = startOfWeek(anchor); const e = addDays(s, 6);
      return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
    }
    if (view === "day") return anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    return "Upcoming 60 days";
  }, [view, anchor]);

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Team View</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black">Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">Everyone's upcoming meetings — one screen.</p>
        </div>
      </div>

      {/* Toolbar */}
      <Card className="rounded-sm bg-card border-border p-3 flex flex-wrap items-center gap-2" data-testid="calendar-toolbar">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => nav(-1)} data-testid="cal-prev"><ChevronLeft className="h-3.5 w-3.5" /></Button>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={today} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => nav(1)} data-testid="cal-next"><ChevronRight className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="font-display text-lg font-extrabold ml-2" data-testid="cal-range-label">{label}</div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="rounded-sm bg-secondary/40 w-56" data-testid="cal-owner-filter"><SelectValue placeholder="All salespeople" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All salespeople ({salespeople.length})</SelectItem>
              {salespeople.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="rounded-sm bg-secondary/40 border border-border p-0.5 flex">
            {VIEWS.map((v) => (
              <button key={v.key} onClick={() => setView(v.key)}
                className={`px-3 py-1.5 rounded-sm text-xs transition-colors ${view === v.key ? "bg-primary/20 text-primary" : "text-muted-foreground"}`}
                data-testid={`cal-view-${v.key}`}>{v.label}</button>
            ))}
          </div>
        </div>
      </Card>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div>
          {isLoading && <div className="text-sm text-muted-foreground text-center py-10">Loading meetings…</div>}
          {!isLoading && view === "month" && <MonthGrid anchor={anchor} rangeStart={rangeStart} meetings={meetings} onPickDay={(d) => { setAnchor(d); setView("day"); }} />}
          {!isLoading && view === "week" && <WeekGrid rangeStart={rangeStart} meetings={meetings} />}
          {!isLoading && view === "day" && <DayGrid anchor={anchor} meetings={meetings} />}
          {!isLoading && view === "list" && <ListView meetings={meetings} />}
        </div>
        <SidePanel upcoming={upcoming} salespeople={salespeople} meetings={meetings} />
      </div>
    </div>
  );
}

function OwnerDot({ owner, size = "sm" }) {
  const c = colorFor(owner?.id);
  const dim = size === "lg" ? "h-6 w-6 text-[10px]" : "h-4 w-4 text-[8px]";
  return (
    <div className={`${dim} rounded-full flex items-center justify-center font-bold flex-none`} style={{ backgroundColor: c.bar, color: "#000" }}>
      {(owner?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
    </div>
  );
}

// ---------------- Month View ----------------
function MonthGrid({ anchor, rangeStart, meetings, onPickDay }) {
  const grid = useMemo(() => {
    const days = [];
    for (let i = 0; i < 42; i++) days.push(addDays(rangeStart, i));
    return days;
  }, [rangeStart]);
  const monthIndex = anchor.getMonth();
  const byDay = useMemo(() => {
    const m = new Map();
    for (const mt of meetings) {
      const d = startOfDay(new Date(mt.start_at)).toISOString();
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(mt);
    }
    return m;
  }, [meetings]);

  const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const today = startOfDay(new Date());

  return (
    <Card className="rounded-sm bg-card border-border p-3" data-testid="cal-month">
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekdays.map((w) => (<div key={w} className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold text-center py-1">{w}</div>))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((d, i) => {
          const inMonth = d.getMonth() === monthIndex;
          const isToday = sameDay(d, today);
          const list = byDay.get(startOfDay(d).toISOString()) || [];
          return (
            <button key={i} onClick={() => onPickDay(d)}
              className={`text-left min-h-[86px] p-1.5 rounded-sm border transition-colors ${inMonth ? "bg-secondary/20 border-border hover:bg-secondary/40" : "bg-card border-border/40 opacity-50 hover:opacity-100"} ${isToday ? "ring-1 ring-primary" : ""}`}
              data-testid={`cal-day-${d.toISOString().slice(0,10)}`}>
              <div className={`text-xs font-semibold ${isToday ? "text-primary" : ""}`}>{d.getDate()}</div>
              <div className="mt-1 space-y-0.5">
                {list.slice(0, 3).map((m) => {
                  const c = colorFor(m.owner?.id);
                  return (
                    <div key={m.id} className="text-[10px] truncate px-1 py-0.5 rounded-sm flex items-center gap-1"
                      style={{ backgroundColor: c.bg, color: c.fg }}
                      title={`${m.meeting_type_name} · ${m.contact_name} · ${m.owner?.name || ""}`}>
                      <span className="opacity-70 flex-none">{fmtTime(m.start_at)}</span>
                      <span className="truncate">{m.contact_name}</span>
                    </div>
                  );
                })}
                {list.length > 3 && <div className="text-[10px] text-muted-foreground pl-1">+{list.length - 3} more</div>}
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------- Week View ----------------
function WeekGrid({ rangeStart, meetings }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(rangeStart, i));
  const startHour = 7, endHour = 22;
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const today = startOfDay(new Date());
  return (
    <Card className="rounded-sm bg-card border-border p-3 overflow-x-auto" data-testid="cal-week">
      <div className="min-w-[820px]">
        <div className="grid grid-cols-[50px_repeat(7,1fr)] gap-1">
          <div />
          {days.map((d, i) => (
            <div key={i} className={`text-center py-2 ${sameDay(d, today) ? "text-primary" : "text-muted-foreground"}`}>
              <div className="text-[10px] uppercase tracking-widest font-bold">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
              <div className="text-lg font-black font-display">{d.getDate()}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-[50px_repeat(7,1fr)] gap-1 mt-2">
          {hours.map((h) => (
            <React.Fragment key={h}>
              <div className="text-[10px] text-muted-foreground text-right pr-2 h-14">{h}:00</div>
              {days.map((d, di) => {
                const cellStart = new Date(d); cellStart.setHours(h, 0, 0, 0);
                const cellEnd = new Date(d); cellEnd.setHours(h + 1, 0, 0, 0);
                const items = meetings.filter((m) => {
                  const s = new Date(m.start_at); return s >= cellStart && s < cellEnd;
                });
                return (
                  <div key={`${h}-${di}`} className="h-14 border-t border-border/60 relative">
                    {items.map((m) => {
                      const c = colorFor(m.owner?.id);
                      const s = new Date(m.start_at); const e = new Date(m.end_at || (s.getTime() + 30 * 60000));
                      const minsFromCellStart = (s - cellStart) / 60000;
                      const durationMins = Math.max(20, (e - s) / 60000);
                      return (
                        <div key={m.id}
                          className="absolute left-0 right-0 mx-0.5 rounded-sm px-1.5 py-1 text-[10px] overflow-hidden cursor-pointer hover:z-10 hover:shadow-lg transition-shadow"
                          style={{
                            top: `${(minsFromCellStart / 60) * 3.5}rem`,
                            height: `${(durationMins / 60) * 3.5}rem`,
                            backgroundColor: c.bg,
                            borderLeft: `2px solid ${c.bar}`,
                            color: c.fg,
                          }}
                          title={`${m.meeting_type_name} · ${m.contact_name} · ${m.owner?.name || ""}`}
                          data-testid={`cal-event-${m.id}`}>
                          <div className="font-semibold truncate">{m.contact_name}</div>
                          <div className="truncate opacity-80">{fmtTime(s)} · {m.owner?.name || ""}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ---------------- Day View ----------------
function DayGrid({ anchor, meetings }) {
  const dayStart = startOfDay(anchor);
  const startHour = 7, endHour = 22;
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const items = meetings.filter((m) => sameDay(new Date(m.start_at), anchor)).sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  return (
    <Card className="rounded-sm bg-card border-border p-4" data-testid="cal-day">
      {items.length === 0 && <div className="text-sm text-muted-foreground text-center py-10">No meetings this day.</div>}
      <div className="grid grid-cols-[70px_1fr] gap-1">
        {hours.map((h) => {
          const cellStart = new Date(dayStart); cellStart.setHours(h, 0, 0, 0);
          const cellEnd = new Date(dayStart); cellEnd.setHours(h + 1, 0, 0, 0);
          const bucket = items.filter((m) => { const s = new Date(m.start_at); return s >= cellStart && s < cellEnd; });
          return (
            <React.Fragment key={h}>
              <div className="text-xs text-muted-foreground pr-3 text-right h-20">{h}:00</div>
              <div className="h-20 border-t border-border/60 relative pl-2">
                {bucket.map((m) => (<DayEventCard key={m.id} m={m} />))}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </Card>
  );
}

function DayEventCard({ m }) {
  const c = colorFor(m.owner?.id);
  return (
    <div className="mb-1 rounded-sm p-2 text-xs border-l-2" style={{ backgroundColor: c.bg, borderLeftColor: c.bar, color: c.fg }} data-testid={`cal-day-event-${m.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold">{m.contact_name}</div>
        <div className="text-[10px] opacity-70">{fmtTime(m.start_at)} – {fmtTime(m.end_at)}</div>
      </div>
      <div className="text-[10px] opacity-80 mt-0.5 flex items-center flex-wrap gap-x-2 gap-y-0.5">
        <span className="flex items-center gap-1"><UserIcon className="h-2.5 w-2.5" /> {m.owner?.name || "—"}</span>
        <span>· {m.meeting_type_name}</span>
        {m.google_meet_link && (<a href={m.google_meet_link} target="_blank" rel="noreferrer" className="flex items-center gap-1 underline hover:opacity-100"><Video className="h-2.5 w-2.5" />Meet</a>)}
        {m.location && !m.google_meet_link && (<span className="flex items-center gap-1"><MapPin className="h-2.5 w-2.5" />{typeof m.location === "string" ? m.location : (m.location.label || m.location.address || m.location.type || "")}</span>)}
      </div>
    </div>
  );
}

// ---------------- List View ----------------
function ListView({ meetings }) {
  const groups = useMemo(() => {
    const m = new Map();
    for (const mt of meetings) {
      const k = startOfDay(new Date(mt.start_at)).toISOString();
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(mt);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [meetings]);

  if (groups.length === 0) {
    return <Card className="rounded-sm bg-card border-border p-10 text-center text-muted-foreground text-sm" data-testid="cal-list-empty">No meetings scheduled in this range.</Card>;
  }
  return (
    <div className="space-y-4" data-testid="cal-list">
      {groups.map(([k, list]) => {
        const d = new Date(k);
        const isToday = sameDay(d, new Date());
        return (
          <Card key={k} className="rounded-sm bg-card border-border p-4">
            <div className="flex items-baseline gap-3 mb-3">
              <div className={`font-display text-2xl font-black ${isToday ? "text-primary" : ""}`}>{d.getDate()}</div>
              <div>
                <div className="text-sm font-semibold">{d.toLocaleDateString(undefined, { weekday: "long" })}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{d.toLocaleDateString(undefined, { month: "short", year: "numeric" })} {isToday ? "· Today" : ""}</div>
              </div>
              <div className="text-xs text-muted-foreground ml-auto">{list.length} meeting{list.length === 1 ? "" : "s"}</div>
            </div>
            <div className="divide-y divide-border/60">
              {list.map((m) => {
                const c = colorFor(m.owner?.id);
                return (
                  <div key={m.id} className="py-2.5 flex items-center gap-3" data-testid={`cal-list-row-${m.id}`}>
                    <OwnerDot owner={m.owner} size="lg" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{m.contact_name} <span className="text-muted-foreground font-normal">· {m.meeting_type_name}</span></div>
                      <div className="text-xs text-muted-foreground flex items-center flex-wrap gap-x-2">
                        <Clock className="h-3 w-3" /> {fmtTime(m.start_at)} – {fmtTime(m.end_at)}
                        <span>·</span>
                        <UserIcon className="h-3 w-3" /> {m.owner?.name || "—"}
                        {m.google_meet_link && (<><span>·</span><a href={m.google_meet_link} target="_blank" rel="noreferrer" className="text-primary hover:underline flex items-center gap-1"><Video className="h-3 w-3" />Meet</a></>)}
                      </div>
                    </div>
                    <Badge className="rounded-sm text-[10px]" style={{ backgroundColor: c.bg, color: c.fg, borderColor: c.bar + "44" }}>{(m.owner?.name || "").split(" ")[0]}</Badge>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ---------------- Side Panel ----------------
function SidePanel({ upcoming, salespeople, meetings }) {
  // Count per salesperson within current view
  const counts = useMemo(() => {
    const c = new Map();
    for (const m of meetings) c.set(m.user_id, (c.get(m.user_id) || 0) + 1);
    return c;
  }, [meetings]);
  return (
    <div className="space-y-4">
      <Card className="rounded-sm bg-card border-border p-4" data-testid="cal-upcoming">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5 mb-2"><CalendarDays className="h-3 w-3" /> Next Up</div>
        {upcoming.length === 0 && <div className="text-xs text-muted-foreground py-3">Nothing scheduled.</div>}
        {upcoming.map((m) => {
          const c = colorFor(m.owner?.id);
          return (
            <div key={m.id} className="flex items-start gap-2 py-2 border-b border-border/60 last:border-0" data-testid={`cal-upcoming-${m.id}`}>
              <div className="w-1 self-stretch rounded-sm" style={{ backgroundColor: c.bar }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{m.contact_name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {new Date(m.start_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {fmtTime(m.start_at)} · {m.owner?.name || "—"}
                </div>
                {m.google_meet_link && (
                  <a href={m.google_meet_link} target="_blank" rel="noreferrer" className="text-[10px] text-primary hover:underline inline-flex items-center gap-1 mt-0.5">
                    <Video className="h-3 w-3" />Join Meet <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </Card>

      <Card className="rounded-sm bg-card border-border p-4">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1.5 mb-2"><Users className="h-3 w-3" /> Team Load</div>
        {salespeople.map((s) => {
          const c = colorFor(s.id);
          const n = counts.get(s.id) || 0;
          return (
            <div key={s.id} className="flex items-center gap-2 py-1.5">
              <OwnerDot owner={s} />
              <div className="text-xs flex-1 truncate">{s.name}</div>
              <Badge className="rounded-sm text-[10px]" style={{ backgroundColor: c.bg, color: c.fg }}>{n}</Badge>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
